// PROVENANCE: C:\Valentin\shared\guide\guide-diagrams.ts @ d32c53d (main, H3a), its types and sheetIn, adapted 2026-10-11 (H3b-6)
// CHANGES: the web app has no zod, so the browser takes only the shape and sheetIn; the server checks the file
//          (server/src/help/guide-diagrams.ts, the format guide-diagrams.v1) before it serves it, and refuses one that fails.

import type { FlowKind, FlowSheet } from './flow-layout'

export type GuideLanguage = 'en' | 'sv'

export interface DiagramNode {
  id: string
  col: number
  row: number
  w?: number
  h?: number
  kind: FlowKind
  routes?: string[]
  rules?: string[]
}

export interface DiagramText {
  title: string
  question: string
  lanes: Record<string, string>
  nodes: Record<string, { label: string; sub?: string; detail: string }>
  edges?: Record<string, string>
}

export interface GuideDiagram {
  id: string
  cols: number
  rows: number
  cellW?: number
  lanes: { id: string; axis: 'col' | 'row'; start: number; span: number }[]
  nodes: DiagramNode[]
  edges: { from: string; to: string; label?: string; style?: 'normal' | 'strong' | 'soft'; exit?: 'left' | 'right' | 'top' | 'bottom'; enter?: 'left' | 'right' | 'top' | 'bottom' }[]
  steps: string[]
  text: { en: DiagramText; sv?: DiagramText }
}

export interface GuideDiagrams {
  format: string
  id: string
  version: string
  status: string
  updated: string
  languages: GuideLanguage[]
  diagrams: GuideDiagram[]
}

/** The diagram in one language, ready to draw (the language's text, else English). */
export function sheetIn(d: GuideDiagram, language: GuideLanguage): FlowSheet {
  const text = d.text[language] ?? d.text.en
  return {
    id: d.id,
    title: text.title,
    question: text.question,
    cols: d.cols,
    rows: d.rows,
    ...(d.cellW ? { cellW: d.cellW } : {}),
    lanes: d.lanes.map((l) => ({ ...l, label: text.lanes[l.id] ?? l.id })),
    nodes: d.nodes.map((n) => {
      const t = text.nodes[n.id]
      return { ...n, label: t?.label ?? n.id, ...(t?.sub ? { sub: t.sub } : {}), ...(t?.detail ? { detail: t.detail } : {}) }
    }),
    edges: d.edges.map((e) => {
      const { label, ...rest } = e
      const words = label ? text.edges?.[label] : undefined
      return words ? { ...rest, label: words } : rest
    }),
    steps: [...d.steps],
  }
}
